import React from 'react';
import Avatar from './Avatar';
import { AccessRole } from '@/types';
import { ReactComponent as CloseButton } from '@/assets/icons/close.svg';
import useUser from '@/hooks/useUser';
import { useTranslation } from 'react-i18next';

type UserCardSize = 'normal' | 'small' | 'smallest';

interface UserCardProps {
	userId?: string;
	avatarUrl?: string | null;
	name?: string | null;
	role?: AccessRole;
	avatarColor?: string;
	size?: UserCardSize;
	className?: string;
	onClose?: () => void;
}

const sizeStyles: Record<UserCardSize, { avatar: 'xs' | 'sm' | 'md'; name: string; close: string }> = {
	normal: {
		avatar: 'md',
		name: 'text-base font-bold pl-2 pr-4',
		close: 'ml-2 p-1 [&_svg]:w-6 [&_svg]:h-6',
	},
	small: {
		avatar: 'sm',
		name: 'text-sm font-bold pl-1.5 pr-3',
		close: 'ml-1 p-0.5 [&_svg]:w-5 [&_svg]:h-5',
	},
	smallest: {
		avatar: 'xs',
		name: 'text-base leading-6 font-bold pl-1 pr-2',
		close: 'ml-1 p-0.5 [&_svg]:w-4 [&_svg]:h-4',
	},
};

const UserCard: React.FC<UserCardProps> = ({ userId, avatarUrl, name, avatarColor, size = 'normal', className, onClose }) => {
	// If userId is provided, fetch the user data
	const { user, isLoading } = useUser(userId);

	const { t } = useTranslation();
	const styles = sizeStyles[size];

	// Use fetched user data if available, otherwise use props
	const displayName = user?.name || name;
	const displayAvatarUrl = user?.other?.avatarUrl || avatarUrl;
	const displayAvatarColor = user?.other?.avatarColor || avatarColor;

	if (isLoading) {
		return (
			<div className={`rounded-md flex flex-row items-center bg-white ${className ?? ''}`}>
				<div className="relative">
					<Avatar
						name={displayName}
						size={styles.avatar}
						avatarColor={displayAvatarColor}
					/>
				</div>
				<div className={`text-black ${styles.name}`}>{t('loading')}</div>
			</div>
		);
	}

	return (
		<div className={`rounded-md flex flex-row items-center bg-gray-200 ${className ?? ''}`}>
			<div className="relative">
				<Avatar
					avatarUrl={displayAvatarUrl}
					name={displayName}
					size={styles.avatar}
					avatarColor={displayAvatarColor}
				/>
			</div>
			<div className={`text-black ${styles.name}`}>{displayName}</div>
			{onClose && (
				<div onClick={onClose} className={`hover:bg-gray-300 rounded-full ${styles.close} [&_svg]:fill-red-600 cursor-pointer`}>
					<CloseButton />
				</div>
			)}
		</div>
	);
};

export default UserCard;
